import React from 'react';
import { createPortal } from 'react-dom';
import { Camera, ChevronLeft, ChevronRight, X, Images } from 'lucide-react';
import { TravelProfile } from '../../../types/matching';
import { getCityPhotos } from '../../../lib/unsplash-service';

interface ProfileDetailPhotosProps {
  profile: TravelProfile;
}

export function ProfileDetailPhotos({ profile }: ProfileDetailPhotosProps) {
  const [destinationPhotos, setDestinationPhotos] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [fullscreenPhoto, setFullscreenPhoto] = React.useState<string | null>(null);

  const photos = profile.photos?.length
    ? profile.photos.slice(0, 6)
    : destinationPhotos.slice(0, 6);

  React.useEffect(() => {
    const fetchDestinationPhotos = async () => {
      if (!profile.photos?.length && profile.preferences.city) {
        try {
          setIsLoading(true);
          const fetched = await getCityPhotos(profile.preferences.city, 6);
          setDestinationPhotos(fetched);
        } catch (error) {
          console.error('Error fetching destination photos:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchDestinationPhotos();
  }, [profile.photos, profile.preferences.city]);

  React.useEffect(() => {
    if (!fullscreenPhoto) return;

    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFullscreenPhoto(null);
      } else if (e.key === 'ArrowLeft') {
        const idx = photos.indexOf(fullscreenPhoto);
        setFullscreenPhoto(photos[(idx - 1 + photos.length) % photos.length]);
      } else if (e.key === 'ArrowRight') {
        const idx = photos.indexOf(fullscreenPhoto);
        setFullscreenPhoto(photos[(idx + 1) % photos.length]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      const savedY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(savedY || '0') * -1);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [fullscreenPhoto, photos]);

  if (!profile.photos?.length && !destinationPhotos.length && !isLoading) return null;
  if (photos.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2.5 mb-4">
        <Images className="w-5 h-5 text-gray-400 dark:text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {profile.photos?.length ? 'Photos' : 'Photos de la destination'}
        </h3>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {photos.map((photo, index) => (
          <div
            key={index}
            className="aspect-square rounded-xl overflow-hidden cursor-pointer relative group"
            onClick={() => setFullscreenPhoto(photo)}
          >
            <img
              src={photo}
              alt={`Photo ${index + 1}`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
          </div>
        ))}
        {isLoading && (
          <div className="aspect-square rounded-xl bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center animate-pulse">
            <Camera className="w-6 h-6 text-gray-300 dark:text-gray-600" />
          </div>
        )}
      </div>

      {fullscreenPhoto && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center animate-fade-in"
          onClick={(e) => {
            e.stopPropagation();
            setFullscreenPhoto(null);
          }}
        >
          <button
            className="fixed top-5 right-5 p-2.5 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
            onClick={(e) => {
              e.stopPropagation();
              setFullscreenPhoto(null);
            }}
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>

          {photos.length > 1 && (
            <div className="fixed top-5 left-1/2 -translate-x-1/2 px-3.5 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white/80 text-xs font-medium z-10">
              {photos.indexOf(fullscreenPhoto) + 1} / {photos.length}
            </div>
          )}

          <img
            src={fullscreenPhoto}
            alt="Photo en plein ecran"
            className="max-h-[92vh] max-w-[92vw] object-contain select-none"
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />

          {photos.length > 1 && (
            <>
              <button
                className="fixed left-4 top-1/2 -translate-y-1/2 p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  const idx = photos.indexOf(fullscreenPhoto);
                  setFullscreenPhoto(photos[(idx - 1 + photos.length) % photos.length]);
                }}
                aria-label="Photo precedente"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                className="fixed right-4 top-1/2 -translate-y-1/2 p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  const idx = photos.indexOf(fullscreenPhoto);
                  setFullscreenPhoto(photos[(idx + 1) % photos.length]);
                }}
                aria-label="Photo suivante"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          {photos.length > 1 && (
            <div className="fixed bottom-5 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto px-3 py-2.5 bg-white/10 backdrop-blur-sm rounded-xl z-10">
              {photos.map((photo, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFullscreenPhoto(photo);
                  }}
                  className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden transition-all ${
                    photo === fullscreenPhoto
                      ? 'ring-2 ring-white scale-105'
                      : 'opacity-50 hover:opacity-90'
                  }`}
                >
                  <img
                    src={photo}
                    alt={`Miniature ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
